import Chip from '@mui/material/Chip';
import { getColorBasedOnIndex, type Search } from '@tyro/api';
import type { AutocompleteProps } from '@tyro/core/autocomplete';
import { Avatar } from '@tyro/core/avatar';
import { useDebouncedValue } from '@tyro/core/hooks';
import { useTranslation } from '@tyro/i18n';
import { useGroupSearch } from '../api/group-search';

export type GroupSearchParty = Pick<
  Search,
  'partyId' | 'text' | 'avatarUrl'
> & {
  type?: Search['type'];
};

export const useGroupSearchProps = (
  customProps?: Partial<AutocompleteProps<GroupSearchParty, true>>,
): AutocompleteProps<GroupSearchParty, true> => {
  const { t } = useTranslation(['common', 'calendar']);

  const {
    value: searchValue,
    setValue: setSearchValue,
    debouncedValue: debouncedSearchValue,
  } = useDebouncedValue({
    defaultValue: '',
  });

  const { data: options, isLoading } = useGroupSearch(debouncedSearchValue);

  return {
    label: t('common:groups'),
    optionIdKey: 'partyId',
    optionTextKey: 'text',
    multiple: true,
    open: searchValue.length > 0,
    filterSelectedOptions: true,
    clearOnBlur: true,
    filterOptions: (x) => x,
    freeSolo: true,
    loading: isLoading,
    options: options ?? [],
    onInputChange: (_, newInputValue) => setSearchValue(newInputValue),
    renderTags: (tags, getTagProps) =>
      tags.map((tag, index) => {
        const { key, ...tagProps } = getTagProps({ index });
        return (
          <Chip
            key={key}
            size="small"
            variant="soft"
            color={getColorBasedOnIndex(index)}
            avatar={<Avatar name={tag.text} src={tag.avatarUrl ?? undefined} />}
            label={tag.text}
            {...tagProps}
          />
        );
      }),
    renderAvatarOption: (option, renderOption) =>
      renderOption({
        name: option.text,
        src: option.avatarUrl ?? undefined,
        caption: option.type ? t(`common:searchType.${option.type}`) : '',
      }),
    ...customProps,
  };
};
